import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Card,
  SectionHeader,
  Tag,
  Button,
  Input,
  Skeleton,
  EmptyState,
} from "../../../design-system";
import { IconChart, IconSparkles } from "../../../design-system/icons/icons";
import {
  RevenueCatApp,
  RevenueCatConfig,
  RevenueCatSummary,
  RevenueCatMetric,
  RevenueCatSubscriber,
} from "../../../types/RevenueCat";
import {
  getRevenueCatConfig,
  saveRevenueCatConfig,
  getRevenueCatSummary,
  listRevenueCatApps,
} from "../../../database/revenuecat/revenueCatApi";
import {
  getStripeConnectStatus,
  getStripeSummary,
  provisionStripePrices,
  setStripeRevenueCatKey,
} from "../../../database/stripe/stripeApi";
import { StripeConnectStatus, StripeSummary } from "../../../types/Stripe";

const SectionSpacing = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-4);
`;

const StatLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const StatValue = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  line-height: var(--line-height-tight);
  margin-top: var(--space-3);
`;

const StatHint = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: var(--space-2);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-sans);
`;

const Th = styled.th`
  text-align: left;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0 var(--space-3) var(--space-2);
  white-space: nowrap;
`;

const Td = styled.td`
  font-size: var(--font-size-sm);
  color: var(--color-text);
  padding: var(--space-3);
  border-top: 1px solid var(--color-border-subtle);
  white-space: nowrap;
`;

const Primary = styled.div`
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
`;

const Sub = styled.div`
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: 2px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Actions = styled.div`
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
  flex-wrap: wrap;
`;

const PriceList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const SelectShell = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 36px;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  cursor: pointer;

  &:focus-within {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-focus);
  }

  span {
    color: var(--color-text-tertiary);
    font-size: var(--font-size-xs);
  }
`;

const NativeSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  cursor: pointer;
  max-width: 220px;
`;

// RevenueCat status -> Tag variant. Anything currently granting access reads as
// positive; trials are informational; cancelled/expired are muted/negative.
const statusVariant = (
  status: string,
  givesAccess: boolean
): "success" | "accent" | "warning" | "neutral" | "danger" => {
  const s = (status || "").toLowerCase();
  if (s === "trialing") return "accent";
  if (s === "expired") return "danger";
  if (s === "in_grace_period" || s === "in_billing_retry" || s === "paused")
    return "warning";
  if (givesAccess || s === "active") return "success";
  return "neutral";
};

const CURRENCY_METRIC = (id: string) =>
  /mrr|revenue/i.test(id);

const metricLabel = (m: RevenueCatMetric): string => {
  // `revenue` is lifetime on the app-scoped path and the dashboard's 28 days
  // on the project-wide path. Older function versions send no period.
  if (m.id === "revenue") {
    if (m.period === "lifetime") return "Lifetime revenue";
    if (m.period === "last_28_days") return "Revenue (28d)";
    return "Revenue";
  }
  const map: Record<string, string> = {
    active_subscriptions: "Active subscriptions",
    active_subscribers: "Active subscribers",
    active_trials: "Active trials",
    active_users: "Active users",
    new_customers: "New customers",
    mrr: "MRR",
    revenue_28d: "Revenue (last 28 days)",
  };
  return map[m.id] || m.name || m.id;
};

const formatCurrency = (value: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency || ""}`.trim();
  }
};

const formatDate = (ms: number | null): string => {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return "—";
  }
};

const subscriberName = (s: RevenueCatSubscriber): string =>
  s.email || s.customerId || "—";

type Props = { communityId?: string };

const RevenueTab: React.FC<Props> = ({ communityId }) => {
  const [config, setConfig] = useState<RevenueCatConfig | null>(null);
  const [summary, setSummary] = useState<RevenueCatSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // App scoping
  const [apps, setApps] = useState<RevenueCatApp[]>([]);

  // Connect / reconfigure form
  const [showForm, setShowForm] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [secretApiKey, setSecretApiKey] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(
    null
  );
  const [stripeSummary, setStripeSummary] = useState<StripeSummary | null>(
    null
  );
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeSaving, setStripeSaving] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeCurrency, setStripeCurrency] = useState("USD");
  const [stripeMonthly, setStripeMonthly] = useState("9.99");
  const [stripeAnnual, setStripeAnnual] = useState("79.99");
  const [stripeWeekly, setStripeWeekly] = useState("");
  // RevenueCat forwarding link (advanced, hidden until opened).
  const [rcKeyInput, setRcKeyInput] = useState("");
  const [rcKeyOpen, setRcKeyOpen] = useState(false);
  const [rcKeySaving, setRcKeySaving] = useState(false);

  const loadSummary = async (cid: string) => {
    setRefreshing(true);
    setError(null);
    try {
      const s = await getRevenueCatSummary(cid);
      setSummary(s);
    } catch (e: any) {
      setError(e?.message || "Could not load RevenueCat data.");
    } finally {
      setRefreshing(false);
    }
  };

  const loadStripe = async (cid: string) => {
    setStripeLoading(true);
    setStripeError(null);
    try {
      const status = await getStripeConnectStatus(cid);
      setStripeStatus(status);
      setStripeCurrency((status.currency || "USD").toUpperCase());
      if (status.connected) {
        // Stats are secondary to the connection status; if they fail the
        // panel still shows the connect state and the error inline.
        try {
          setStripeSummary(await getStripeSummary(cid));
        } catch (e: any) {
          setStripeSummary(null);
          setStripeError(e?.message || "Could not load Stripe revenue stats.");
        }
      } else {
        setStripeSummary(null);
      }
    } catch (e: any) {
      setStripeError(e?.message || "Could not load Stripe status.");
    } finally {
      setStripeLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!communityId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const cfg = await getRevenueCatConfig(communityId);
        if (cancelled) return;
        setConfig(cfg);
        setProjectId(cfg.projectId || "");
        setCurrency(cfg.currency || "USD");
        await loadStripe(communityId);
        if (cfg.connected) {
          // Apps power the scope picker; load alongside the summary. Best-effort
          // (a missing apps:read scope shouldn't block the metrics).
          await Promise.all([
            loadSummary(communityId),
            listRevenueCatApps(communityId)
              .then((a) => {
                if (!cancelled) setApps(a);
              })
              .catch(() => undefined),
          ]);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Could not load RevenueCat.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  const handleSave = async () => {
    if (!communityId) return;
    setSaving(true);
    setFormError(null);
    try {
      const cfg = await saveRevenueCatConfig({
        communityId,
        projectId: projectId.trim(),
        secretApiKey: secretApiKey.trim() || undefined,
        currency: currency.trim() || "USD",
      });
      setConfig(cfg);
      setSecretApiKey("");
      setShowForm(false);
      if (cfg.connected) await loadSummary(communityId);
    } catch (e: any) {
      setFormError(e?.message || "Could not save RevenueCat credentials.");
    } finally {
      setSaving(false);
    }
  };

  // Switch which app the figures are scoped to. Persists the choice (so it
  // sticks) and reloads. Empty appId means the whole project.
  const handleAppChange = async (nextAppId: string) => {
    if (!communityId || !config?.projectId) return;
    const app = apps.find((a) => a.id === nextAppId);
    setRefreshing(true);
    setError(null);
    try {
      const cfg = await saveRevenueCatConfig({
        communityId,
        projectId: config.projectId,
        appId: nextAppId,
        appName: app?.name || "",
      });
      setConfig(cfg);
      await loadSummary(communityId);
    } catch (e: any) {
      setError(e?.message || "Could not switch app.");
      setRefreshing(false);
    }
  };

  const handleLinkRevenueCat = async () => {
    if (!communityId) return;
    setRcKeySaving(true);
    setStripeError(null);
    try {
      const status = await setStripeRevenueCatKey({
        communityId,
        rcStripePublicKey: rcKeyInput.trim(),
      });
      setStripeStatus(status);
      setRcKeyInput("");
      setRcKeyOpen(false);
    } catch (e: any) {
      setStripeError(e?.message || "Could not save the RevenueCat key.");
    } finally {
      setRcKeySaving(false);
    }
  };

  const toCents = (value: string): number | null => {
    if (!value.trim()) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.round(parsed * 100);
  };

  const handleProvisionStripe = async () => {
    if (!communityId) return;
    const plans = [
      { interval: "month" as const, amount: toCents(stripeMonthly) },
      { interval: "year" as const, amount: toCents(stripeAnnual) },
      { interval: "week" as const, amount: toCents(stripeWeekly) },
    ].filter((plan): plan is { interval: "month" | "year" | "week"; amount: number } =>
      typeof plan.amount === "number"
    );

    if (!plans.length) {
      setStripeError("Enter at least one Stripe plan amount.");
      return;
    }

    setStripeSaving(true);
    setStripeError(null);
    try {
      await provisionStripePrices({
        communityId,
        plans: plans.map((plan) => ({
          ...plan,
          currency: stripeCurrency.toLowerCase(),
        })),
      });
      await loadStripe(communityId);
    } catch (e: any) {
      setStripeError(e?.message || "Could not provision Stripe prices.");
    } finally {
      setStripeSaving(false);
    }
  };

  const subscribers = summary?.subscribers ?? [];
  const currencyCode = summary?.currency || config?.currency || "USD";

  const orderedMetrics = useMemo(() => {
    const list = summary?.metrics ?? [];
    const order = [
      "mrr",
      "active_subscriptions",
      "active_subscribers",
      "active_trials",
      "revenue_28d",
      "revenue",
      "active_users",
      "new_customers",
    ];
    return [...list].sort(
      (a, b) =>
        (order.indexOf(a.id) === -1 ? 99 : order.indexOf(a.id)) -
        (order.indexOf(b.id) === -1 ? 99 : order.indexOf(b.id))
    );
  }, [summary]);

  const stripeCheckoutLive = stripeStatus?.subscriptions?.configurationReady === true;

  const stripePanel = (
    <Card padding="lg">
      <SectionHeader
        title="Stripe web checkout"
        description="Manage this community's web subscription prices. Connect or rotate credentials in Settings → Payments. Payments go directly to the coach's Stripe account with no platform fee."
        actions={
          <Button
            variant="ghost"
            onClick={() => communityId && loadStripe(communityId)}
            disabled={stripeLoading}
          >
            Refresh
          </Button>
        }
      />
      <SectionSpacing>
        <Actions>
          <Tag
            variant={stripeCheckoutLive ? "success" : stripeStatus?.connected ? "warning" : "neutral"}
            withDot
          >
            {stripeCheckoutLive
              ? "Membership configuration verified"
              : stripeStatus?.connected
              ? stripeStatus.onboardingComplete
                ? "Connected"
                : "Webhook pending"
              : "Not connected"}
          </Tag>
          {stripeStatus?.livemode === false && (
            <Tag variant="warning">Test mode key</Tag>
          )}
          {!!stripeStatus?.accountId && (
            <Tag variant="info">{stripeStatus.accountId}</Tag>
          )}
        </Actions>
        {stripeError && (
          <Tag variant="danger" withDot>
            {stripeError}
          </Tag>
        )}
        <p><a href="#/settings?tab=payments">Connect, rotate, or test Stripe in Settings → Payments</a>. One-time digital products do not require the subscription prices below.</p>
        {stripeStatus?.connected && (
          <>
            <FormRow>
              <Input
                label="Monthly price"
                placeholder="9.99"
                value={stripeMonthly}
                onChange={(e) => setStripeMonthly(e.target.value)}
                fullWidth
              />
              <Input
                label="Annual price"
                placeholder="79.99"
                value={stripeAnnual}
                onChange={(e) => setStripeAnnual(e.target.value)}
                fullWidth
              />
            </FormRow>
            <FormRow>
              <Input
                label="Weekly price"
                placeholder="Optional"
                value={stripeWeekly}
                onChange={(e) => setStripeWeekly(e.target.value)}
                fullWidth
              />
              <Input
                label="Currency"
                placeholder="USD"
                value={stripeCurrency}
                onChange={(e) => setStripeCurrency(e.target.value.toUpperCase())}
                fullWidth
              />
            </FormRow>
            <Actions>
              <Button
                variant="primary"
                onClick={handleProvisionStripe}
                disabled={stripeSaving || !stripeStatus.onboardingComplete}
              >
                {stripeSaving ? "Saving…" : "Provision Stripe prices"}
              </Button>
              {!stripeStatus.onboardingComplete && (
                <StatHint>Reconnect the Stripe key so the webhook can be registered before prices go live.</StatHint>
              )}
            </Actions>
            {!!stripeStatus.prices?.length && (
              <PriceList>
                {stripeStatus.prices.map((price) => (
                  <Tag key={price.plan} variant="neutral">
                    {price.plan}: {formatCurrency(price.amount / 100, price.currency)}
                  </Tag>
                ))}
              </PriceList>
            )}
            <Actions>
              {stripeStatus.revenueCatLinked ? (
                <>
                  <Tag variant="success" withDot>
                    Forwarding to RevenueCat
                  </Tag>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRcKeyOpen((open) => !open)}
                  >
                    Change key
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRcKeyOpen((open) => !open)}
                  >
                    Link RevenueCat…
                  </Button>
                  <StatHint>
                    Optional. Forwards web purchases into RevenueCat so the
                    subscription metrics above include them.
                  </StatHint>
                </>
              )}
            </Actions>
            {rcKeyOpen && (
              <>
                <FormRow>
                  <Input
                    label="RevenueCat Stripe public key"
                    type="password"
                    placeholder="strp_..."
                    value={rcKeyInput}
                    onChange={(e) => setRcKeyInput(e.target.value)}
                    helperText="RevenueCat dashboard: add a Stripe app under Project settings, Apps, then copy its public API key from Project settings, API keys."
                    fullWidth
                  />
                </FormRow>
                <Actions>
                  <Button
                    variant="primary"
                    onClick={handleLinkRevenueCat}
                    disabled={rcKeySaving || !rcKeyInput.trim()}
                  >
                    {rcKeySaving ? "Saving…" : "Save key"}
                  </Button>
                  {stripeStatus.revenueCatLinked && (
                    <Button
                      variant="ghost"
                      onClick={async () => {
                        setRcKeyInput("");
                        if (!communityId) return;
                        setRcKeySaving(true);
                        setStripeError(null);
                        try {
                          const status = await setStripeRevenueCatKey({
                            communityId,
                            rcStripePublicKey: "",
                          });
                          setStripeStatus(status);
                          setRcKeyOpen(false);
                        } catch (e: any) {
                          setStripeError(
                            e?.message || "Could not unlink RevenueCat."
                          );
                        } finally {
                          setRcKeySaving(false);
                        }
                      }}
                      disabled={rcKeySaving}
                    >
                      Unlink
                    </Button>
                  )}
                </Actions>
              </>
            )}
            {stripeSummary?.configured && (
              <>
                <StatGrid>
                  {stripeSummary.metrics.map((m) => (
                    <Card key={m.id} padding="md">
                      <StatLabel>{metricLabel(m)}</StatLabel>
                      <StatValue>
                        {m.value === null
                          ? "—"
                          : CURRENCY_METRIC(m.id)
                          ? formatCurrency(m.value, stripeSummary.currency)
                          : m.value.toLocaleString()}
                      </StatValue>
                      <StatHint>Web checkout</StatHint>
                    </Card>
                  ))}
                </StatGrid>
                {stripeSummary.truncated && (
                  <StatHint>
                    This Stripe account has more data than one pull covers, so
                    the figures above may undercount.
                  </StatHint>
                )}
                {stripeSummary.subscribers.length > 0 && (
                  <div style={{ overflowX: "auto" }}>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Customer</Th>
                          <Th>Plan</Th>
                          <Th>Status</Th>
                          <Th>Renews</Th>
                          <Th>Price</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {stripeSummary.subscribers.map((s) => (
                          <tr key={s.subscriptionId}>
                            <Td>
                              <Primary>{s.email || s.customerId || "—"}</Primary>
                              {s.email && <Sub>{s.customerId}</Sub>}
                            </Td>
                            <Td>{s.plan}</Td>
                            <Td>
                              <Tag
                                variant={statusVariant(
                                  s.status,
                                  s.status === "active"
                                )}
                                size="sm"
                              >
                                {s.status}
                              </Tag>
                              {s.cancelAtPeriodEnd && <Sub>cancels</Sub>}
                            </Td>
                            <Td>{formatDate(s.currentPeriodEndsAt)}</Td>
                            <Td>
                              {typeof s.amount === "number"
                                ? formatCurrency(s.amount, s.currency)
                                : "—"}
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </SectionSpacing>
    </Card>
  );

  if (!communityId) {
    return (
      <EmptyState
        title="No community selected"
        description="Sign in to a community to view its RevenueCat subscriptions."
      />
    );
  }

  if (loading) {
    return (
      <SectionSpacing>
        <StatGrid>
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} padding="md">
              <Skeleton width="50%" height={14} />
              <div style={{ marginTop: "var(--space-3)" }}>
                <Skeleton width="70%" height={28} />
              </div>
            </Card>
          ))}
        </StatGrid>
        <Card padding="lg">
          <Skeleton width="100%" height={180} />
        </Card>
      </SectionSpacing>
    );
  }

  const connectForm = (
    <Card padding="lg">
      <SectionHeader
        title={config?.connected ? "RevenueCat credentials" : "Connect RevenueCat"}
        description="Add your RevenueCat project id and a v2 secret API key (starts with sk_). The key is stored securely server-side and never shown again."
      />
      <SectionSpacing>
        <FormRow>
          <Input
            label="Project ID"
            placeholder="proj1ab2c3d4"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            fullWidth
          />
          <Input
            label="Currency"
            placeholder="USD"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            fullWidth
          />
        </FormRow>
        <Input
          label="Secret API key (v2)"
          type="password"
          placeholder={
            config?.connected ? "Leave blank to keep the current key" : "sk_..."
          }
          value={secretApiKey}
          onChange={(e) => setSecretApiKey(e.target.value)}
          helperText="RevenueCat dashboard → Project settings → API keys → Secret keys (V2)."
          error={formError || undefined}
          fullWidth
        />
        <Actions>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !projectId.trim()}
          >
            {saving ? "Validating…" : config?.connected ? "Save" : "Connect"}
          </Button>
          {config?.connected && (
            <Button
              variant="ghost"
              onClick={() => {
                setShowForm(false);
                setFormError(null);
                setSecretApiKey("");
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
        </Actions>
      </SectionSpacing>
    </Card>
  );

  // Not configured yet, or the admin opened the reconfigure form.
  if (!config?.connected || showForm) {
    if (!config?.connected && !showForm) {
      return (
        <SectionSpacing>
          {stripePanel}
          <EmptyState
            icon={<IconChart size={20} />}
            title="Subscriptions from RevenueCat"
            description="Connect this community's RevenueCat project to see active subscriptions, MRR, revenue, trials and a live subscriber list."
            actions={
              <Button variant="primary" onClick={() => setShowForm(true)}>
                Connect RevenueCat
              </Button>
            }
          />
        </SectionSpacing>
      );
    }
    return <SectionSpacing>{stripePanel}{connectForm}</SectionSpacing>;
  }

  return (
    <SectionSpacing>
      {stripePanel}
      {error && (
        <Card padding="md">
          <Tag variant="danger" withDot>
            Error
          </Tag>
          <p
            style={{
              marginTop: "var(--space-2)",
              color: "var(--color-text-secondary)",
            }}
          >
            {error}
          </p>
        </Card>
      )}

      <Toolbar>
        {config?.usingSharedKey ? (
          // The shared key only reads the apps Tribed assigned, so the picker
          // would be inert here.
          <span style={{ color: "var(--color-text-secondary)" }}>
            Apps are set by Tribed for this brand.
          </span>
        ) : (
          <SelectShell>
            <span>App</span>
            <NativeSelect
              value={config?.appId || ""}
              onChange={(e) => handleAppChange(e.target.value)}
              disabled={refreshing}
            >
              <option value="">All apps (whole project)</option>
              {config?.appId &&
                !apps.some((a) => a.id === config.appId) && (
                  <option value={config.appId}>
                    {config.appName || config.appId}
                  </option>
                )}
              {apps.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </NativeSelect>
          </SelectShell>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => loadSummary(communityId)}
          disabled={refreshing}
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </Toolbar>

      {config?.usingSharedKey && summary?.appFilterApplied === false && (
        <Card padding="md">
          <Tag variant="warning" withDot>
            Not scoped yet
          </Tag>
          <p
            style={{
              marginTop: "var(--space-2)",
              color: "var(--color-text-secondary)",
            }}
          >
            RevenueCat could not scope metrics to this brand&apos;s apps yet.
          </p>
        </Card>
      )}

      {!config?.usingSharedKey &&
        summary?.appId &&
        summary?.appFilterApplied === false && (
        <Card padding="md">
          <Tag variant="warning" withDot>
            Project-wide metrics
          </Tag>
          <p
            style={{
              marginTop: "var(--space-2)",
              color: "var(--color-text-secondary)",
            }}
          >
            RevenueCat didn&apos;t expose an app or product filter for this
            project&apos;s charts, so the numbers below are for the whole
            project. The subscriber list is still scoped to this app.
          </p>
        </Card>
      )}

      <StatGrid>
        {orderedMetrics.length === 0 ? (
          <Card padding="md">
            <StatLabel>No metrics returned</StatLabel>
            <StatHint>RevenueCat returned no metrics yet.</StatHint>
          </Card>
        ) : (
          orderedMetrics.map((m) => (
            <Card key={m.id} padding="md">
              <StatLabel>{metricLabel(m)}</StatLabel>
              <StatValue>
                {m.value === null
                  ? "—"
                  : CURRENCY_METRIC(m.id)
                  ? formatCurrency(m.value, currencyCode)
                  : m.value.toLocaleString()}
              </StatValue>
              {m.unit && m.value !== null && !CURRENCY_METRIC(m.id) && (
                <StatHint>{m.unit}</StatHint>
              )}
            </Card>
          ))
        )}
      </StatGrid>

      <Card padding="lg">
        <SectionHeader
          title="Subscribers"
          description={
            summary?.appId
              ? `Customers with a subscription to this app${
                  summary?.hasMore
                    ? `, among the latest ${subscribers.length} scanned`
                    : ` (${subscribers.length})`
                }.`
              : summary?.hasMore
              ? `Showing the latest ${subscribers.length} customers. RevenueCat has more.`
              : `${subscribers.length} customer${
                  subscribers.length === 1 ? "" : "s"
                }.`
          }
          actions={
            <Actions>
              {config?.appId && config?.appName && (
                <Tag variant="accent" size="sm">
                  {config.appName}
                </Tag>
              )}
              <Tag variant="neutral" size="sm">
                {currencyCode}
              </Tag>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowForm(true)}
              >
                Reconfigure
              </Button>
            </Actions>
          }
        />

        {subscribers.length === 0 ? (
          <EmptyState
            icon={<IconSparkles size={20} />}
            title="No subscribers yet"
            description="Once customers subscribe in the app they'll appear here."
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table>
              <thead>
                <tr>
                  <Th>Customer</Th>
                  <Th>Plan</Th>
                  <Th>Status</Th>
                  <Th>Renews</Th>
                  <Th>Revenue</Th>
                  <Th>Source</Th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((s) => (
                  <tr key={s.customerId}>
                    <Td>
                      <Primary>{subscriberName(s)}</Primary>
                      {s.email && <Sub>{s.customerId}</Sub>}
                    </Td>
                    <Td>
                      <Primary>{s.productId || "—"}</Primary>
                      {s.store && <Sub>{s.store}</Sub>}
                    </Td>
                    <Td>
                      <Tag
                        variant={statusVariant(s.status, s.givesAccess)}
                        size="sm"
                      >
                        {s.status}
                      </Tag>
                      {s.autoRenewalStatus === "will_not_renew" && (
                        <Sub>cancels</Sub>
                      )}
                    </Td>
                    <Td>{formatDate(s.currentPeriodEndsAt)}</Td>
                    <Td>
                      {typeof s.revenueUsd === "number"
                        ? formatCurrency(s.revenueUsd, "USD")
                        : "—"}
                    </Td>
                    <Td>
                      <Primary>{s.platform || "—"}</Primary>
                      {(s.country || s.environment === "sandbox") && (
                        <Sub>
                          {[
                            s.country,
                            s.environment === "sandbox" ? "sandbox" : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Sub>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </SectionSpacing>
  );
};

export default RevenueTab;
